import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  getAllCategories, createCategory, updateCategory, deleteCategory
} from "api/canblog/CANblogAPI";

function CategoriesManager({ open, onClose }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    categoryName: "",
    slug: "",
    description: ""
  });

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await getAllCategories();
      setCategories(data || []);
      setError(null);
    } catch (err) {
      setError("Failed to load categories");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      categoryName: name,
      slug: generateSlug(name)
    }));
  };

  const handleFieldChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const resetForm = () => {
    setFormData({ categoryName: "", slug: "", description: "" });
    setEditingId(null);
    setIsAdding(false);
  };

  const handleAdd = () => {
    resetForm();
    setIsAdding(true);
  };

  const handleEdit = (category) => {
    setFormData({
      categoryName: category.CategoryName,
      slug: category.Slug,
      description: category.Description || ""
    });
    setEditingId(category.CategoryId);
    setIsAdding(false);
  };

  const handleSave = async () => {
    if (!formData.categoryName.trim() || !formData.slug.trim()) {
      setError("Name and slug are required");
      return;
    }

    try {
      if (editingId) {
        await updateCategory({
          id: editingId,
          categoryName: formData.categoryName,
          slug: formData.slug,
          description: formData.description
        });
      } else {
        await createCategory({
          categoryName: formData.categoryName,
          slug: formData.slug,
          description: formData.description
        });
      }
      resetForm();
      fetchCategories();
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to save category");
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      await deleteCategory(categoryId);
      fetchCategories();
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to delete category");
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Manage Categories</Typography>
          {!isAdding && !editingId && (
            <Button startIcon={<AddIcon />} onClick={handleAdd} variant="contained" size="small">
              Add Category
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editingId ? "Edit Category" : "New Category"}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Name"
                value={formData.categoryName}
                onChange={handleNameChange}
                required
                sx={{ flex: 1, minWidth: 150 }}
              />
              <TextField
                size="small"
                label="Slug"
                value={formData.slug}
                onChange={handleFieldChange("slug")}
                required
                sx={{ flex: 1, minWidth: 150 }}
              />
              <TextField
                size="small"
                label="Description"
                value={formData.description}
                onChange={handleFieldChange("description")}
                sx={{ flex: 2, minWidth: 200 }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton color="primary" onClick={handleSave} title="Save">
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={handleCancel} title="Cancel">
                  <CancelIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* Categories Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No categories found</TableCell>
                  </TableRow>
                ) : (
                  categories.map((cat) => (
                    <TableRow key={cat.CategoryId} hover>
                      <TableCell>{cat.CategoryName}</TableCell>
                      <TableCell>{cat.Slug}</TableCell>
                      <TableCell>{cat.Description || "-"}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(cat)}
                          disabled={editingId === cat.CategoryId}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(cat.CategoryId)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default CategoriesManager;
